import React, {useCallback, useState} from "react";
import {apiV2} from "@/api/axiosConfig";
import {SearchInput} from "@/components/search-input";
import {useDebounce} from "use-debounce";
import {useQuery} from "react-query";
import {TagSelectorModal} from "./TagSelectorModal";

type Tag = [string, string]; // [slug, label]

type TagSelectorProps = {
    includedTags: Tag[];
    excludedTags: Tag[];
    tagMode: "all" | "any";
    onIncludedTagsChange: (tags: Tag[]) => void;
    onExcludedTagsChange: (tags: Tag[]) => void;
    onTagModeChange: (mode: "all" | "any") => void;
}

export function TagSelector({
    includedTags,
    excludedTags,
    tagMode,
    onIncludedTagsChange,
    onExcludedTagsChange,
    onTagModeChange
}: TagSelectorProps) {
    const [search, setSearch] = useState("");
    const [debouncedSearch] = useDebounce(search, 100);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<"include" | "exclude">("include");

    const {data: searchResults = []} = useQuery(
        ["incident-tags-search", debouncedSearch],
        async () => {
            if (!debouncedSearch) return [];
            const response = await apiV2.get<{tags: Tag[]}>("incident-tags", {
                params: {search: debouncedSearch, limit: 20}
            });
            return response.data.tags || [];
        },
        {
            enabled: !!debouncedSearch,
            staleTime: 5 * 60 * 1000,
        }
    );

    const handleAddToInclude = useCallback((tag: Tag) => {
        // Don't add if already included
        if (includedTags.some(t => t[0] === tag[0])) return;
        // Remove from excluded if it's there
        onExcludedTagsChange(excludedTags.filter(t => t[0] !== tag[0]));
        onIncludedTagsChange([...includedTags, tag]);
        setSearch("");
    }, [includedTags, excludedTags, onIncludedTagsChange, onExcludedTagsChange]);

    const handleAddToExclude = useCallback((tag: Tag) => {
        // Don't add if already excluded
        if (excludedTags.some(t => t[0] === tag[0])) return;
        // Remove from included if it's there
        onIncludedTagsChange(includedTags.filter(t => t[0] !== tag[0]));
        onExcludedTagsChange([...excludedTags, tag]);
        setSearch("");
    }, [includedTags, excludedTags, onIncludedTagsChange, onExcludedTagsChange]);

    const handleRemoveFromInclude = useCallback((slug: string) => {
        onIncludedTagsChange(includedTags.filter(t => t[0] !== slug));
    }, [includedTags, onIncludedTagsChange]);

    const handleRemoveFromExclude = useCallback((slug: string) => {
        onExcludedTagsChange(excludedTags.filter(t => t[0] !== slug));
    }, [excludedTags, onExcludedTagsChange]);

    const isTagAlreadySelected = useCallback((slug: string) => {
        return includedTags.some(t => t[0] === slug) || excludedTags.some(t => t[0] === slug);
    }, [includedTags, excludedTags]);

    const handleOpenModal = useCallback((mode: "include" | "exclude") => {
        setModalMode(mode);
        setIsModalOpen(true);
    }, []);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
                <label className="font-medium">Search Tags</label>
                <SearchInput
                    initialValue={search}
                    onChange={setSearch}
                />
                {search && searchResults.length > 0 && (
                    <div className="bg-white border border-light-light-grey rounded shadow-md max-h-60 overflow-y-auto">
                        {searchResults.map((tag) => (
                            <div
                                key={tag[0]}
                                className={`flex justify-between items-center px-4 py-2 border-b border-light-light-grey last:border-b-0 ${
                                    isTagAlreadySelected(tag[0]) ? "bg-desaturated-grey" : "hover:bg-ice"
                                }`}
                            >
                                <span className={isTagAlreadySelected(tag[0]) ? "text-grey" : ""}>
                                    {tag[1]}
                                </span>
                                {!isTagAlreadySelected(tag[0]) && (
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleAddToInclude(tag)}
                                            className="text-xs px-2 py-1 bg-blue text-white rounded hover:bg-dark-blue"
                                        >
                                            Include
                                        </button>
                                        <button
                                            onClick={() => handleAddToExclude(tag)}
                                            className="text-xs px-2 py-1 bg-red text-white rounded hover:bg-dark-red"
                                        >
                                            Exclude
                                        </button>
                                    </div>
                                )}
                                {isTagAlreadySelected(tag[0]) && (
                                    <span className="text-xs text-grey">Already selected</span>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 gap-4">
                {/* Include Column */}
                <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                        <label className="font-medium">Include Tags</label>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handleOpenModal("include")}
                                className="text-xs px-2 py-1 bg-blue text-white rounded hover:bg-dark-blue"
                            >
                                Browse
                            </button>
                            <select
                                value={tagMode}
                                onChange={(e) => onTagModeChange(e.target.value as "all" | "any")}
                                className="text-sm border border-light-grey rounded px-2 py-1"
                            >
                                <option value="any">Match Any</option>
                                <option value="all">Match All</option>
                            </select>
                        </div>
                    </div>
                    <div className="border border-light-light-grey rounded p-3 min-h-[200px] bg-white">
                        {includedTags.length === 0 ? (
                            <div className="text-grey text-sm text-center py-4">
                                No tags included
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {includedTags.map((tag) => (
                                    <div
                                        key={tag[0]}
                                        className="flex items-center gap-2 px-3 py-1.5 rounded text-sm bg-light-blue"
                                    >
                                        <span>{tag[1]}</span>
                                        <button
                                            onClick={() => handleRemoveFromInclude(tag[0])}
                                            className="text-dark-blue hover:text-blue font-bold leading-none"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Exclude Column */}
                <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                        <label className="font-medium">Exclude Tags</label>
                        <button
                            onClick={() => handleOpenModal("exclude")}
                            className="text-xs px-2 py-1 bg-red text-white rounded hover:bg-dark-red"
                        >
                            Browse
                        </button>
                    </div>
                    <div className="border border-light-light-grey rounded p-3 min-h-[200px] bg-white">
                        {excludedTags.length === 0 ? (
                            <div className="text-grey text-sm text-center py-4">
                                No tags excluded
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {excludedTags.map((tag) => (
                                    <div
                                        key={tag[0]}
                                        className="flex items-center gap-2 px-3 py-1.5 rounded text-sm bg-light-red"
                                    >
                                        <span>{tag[1]}</span>
                                        <button
                                            onClick={() => handleRemoveFromExclude(tag[0])}
                                            className="text-dark-red hover:text-red font-bold leading-none"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <TagSelectorModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                includedTags={includedTags}
                excludedTags={excludedTags}
                onIncludedTagsChange={onIncludedTagsChange}
                onExcludedTagsChange={onExcludedTagsChange}
                mode={modalMode}
            />
        </div>
    );
}
